import { Activity, useCallback } from "react";
import cx from "classnames";
import { useShallow } from "zustand/react/shallow";

import Box from "ds/components/Box";
import { useContainerSizeObserver } from "hooks/useContainerBreakpoint";

import { ASSISTANT_PANEL_CONTAINER } from "./constants";
import useAssistantPanelStore from "./useStore";
import useSidebarResize from "./useSidebarResize";
import useAssistantPanelResponsiveVariant from "./hooks/useResponsiveVariant";
import styles from "./styles.module.css";
import useFloatingResize from "./useFloatingResize";

type AssistantPanelLayoutProps = {
  children: React.ReactNode;
};

// Elements that natively take focus on mousedown and therefore don't need us to
// intervene. Anything else (plain text, paragraphs, headings, etc.) would
// otherwise blur the open modal/drawer's focused control to <body>, which
// triggers React Aria's FocusScope to restore focus back into the modal mid-drag
// and cancel the in-progress text selection.
const FOCUSABLE_SELECTOR =
  'button, a[href], input, textarea, select, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

const AssistantPanelLayout = ({ children }: AssistantPanelLayoutProps) => {
  useAssistantPanelResponsiveVariant();

  const { variant, isOpen, floatingPosition, floatingSize, isSidebarResizing } =
    useAssistantPanelStore(
      useShallow((state) => ({
        variant: state.variant,
        isOpen: state.isOpen,
        floatingPosition: state.floatingPosition,
        floatingSize: state.floatingSize,
        isSidebarResizing: state.isSidebarResizing,
      }))
    );

  const isFullscreen = variant === "fullscreen";
  const isFloating = variant === "floating";
  const isFloatingLike = isFloating || isFullscreen;

  const { onStart: onSidebarResizeStart } = useSidebarResize();
  const { onStart: onFloatingResizeStart } = useFloatingResize();

  const { ref: wrapperRef, setRef } = useContainerSizeObserver(ASSISTANT_PANEL_CONTAINER);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      if (!wrapperRef.current || !target || target.closest(FOCUSABLE_SELECTOR)) {
        return;
      }

      if (wrapperRef.current.contains(document.activeElement)) {
        return;
      }

      wrapperRef.current.focus({ preventScroll: true });
    },
    [wrapperRef]
  );

  const style = isFloating
    ? {
        left: floatingPosition.x,
        top: floatingPosition.y,
        width: floatingSize.width,
        height: floatingSize.height,
      }
    : undefined;

  return (
    <Activity mode={isOpen ? "visible" : "hidden"}>
      <Box
        ref={setRef}
        direction="column"
        className={cx(styles.wrapper, styles[isFullscreen ? "floating" : variant], {
          [styles.fullscreen]: isFullscreen,
        })}
        style={style}
        data-react-aria-top-layer="true"
        data-ai-chat-layout={variant}
        tabIndex={-1}
        onMouseDown={handleMouseDown}
        fullWidth
      >
        {variant === "side" && (
          <div
            className={cx(styles.resizeBar, { [styles.resizing]: isSidebarResizing })}
            onPointerDown={onSidebarResizeStart}
            role="presentation"
          />
        )}

        {isFloatingLike && (
          <>
            <div
              className={styles.resizeN}
              onPointerDown={onFloatingResizeStart("n")}
              role="presentation"
            />
            <div
              className={styles.resizeS}
              onPointerDown={onFloatingResizeStart("s")}
              role="presentation"
            />
            <div
              className={styles.resizeE}
              onPointerDown={onFloatingResizeStart("e")}
              role="presentation"
            />
            <div
              className={styles.resizeW}
              onPointerDown={onFloatingResizeStart("w")}
              role="presentation"
            />
            {/* Corner handles */}
            <div
              className={styles.resizeNE}
              onPointerDown={onFloatingResizeStart("ne")}
              role="presentation"
            />
            <div
              className={styles.resizeNW}
              onPointerDown={onFloatingResizeStart("nw")}
              role="presentation"
            />
            <div
              className={styles.resizeSE}
              onPointerDown={onFloatingResizeStart("se")}
              role="presentation"
            />
            <div
              className={styles.resizeSW}
              onPointerDown={onFloatingResizeStart("sw")}
              role="presentation"
            />
          </>
        )}

        {children}
      </Box>
    </Activity>
  );
};

export default AssistantPanelLayout;
